import React, { useState, useEffect, useRef } from 'react';
import { useActiveCareerFocus } from '../../hooks/useCareerFocus';
import { useCareerFocusFeatures } from '../../hooks/useStatsigFeatures';
import { useDarkMode } from '../../context/DarkModeContext';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { createLogger } from '../../utils/logger';
import StandardFocusHeader from '../Focus/StandardFocusHeader';
import { PlusIcon } from '@heroicons/react/24/outline';
import TimelineProgress from './TimelineProgress';
import MissionControl from './MissionControl';
import TabContainer from './TabContainer';
import ProgressWidgets from './ProgressWidgets';
import TourOverlay, { TourStep } from '../shared/TourOverlay';
import { useGoals } from '../../hooks/useGoals';
import { useCareerCards } from '../../hooks/useCareerCards';
import { useRubric } from '../../hooks/useRubric';
import EnhancedQuickAddModal from '../QuickNote/EnhancedQuickAddModal';
import { useInternshipPhase } from '../../hooks/useInternshipPhase';
import { useDashboardTourStatus } from '../../hooks/useDashboardTourStatus';
import { PhaseGuidanceProvider } from '../../context/PhaseGuidanceContext';
import MultipleFocusWarning from '../Focus/MultipleFocusWarning';
import { useDefaultFocus } from '../../hooks/useDefaultFocus';

const logger = createLogger({ module: 'InternshipDashboardV2' });

export type InternshipPhase = 'onboarding' | 'foundation' | 'growth' | 'impact' | 'transition';

interface InternshipDashboardV2Props {
  onOpenToDoModal?: () => void;
  onOpenQuickNote?: () => void;
  isCompleted?: boolean;
  completionMetadata?: {
    daysSinceEnd?: number;
    completedAt?: string;
  };
}

const InternshipDashboardV2: React.FC<InternshipDashboardV2Props> = ({
  onOpenToDoModal,
  onOpenQuickNote,
  isCompleted = false,
  completionMetadata
}) => {
  const { focusId } = useParams<{ focusId?: string }>();
  const { focus, loading: focusLoading, error: focusError } = useActiveCareerFocus('internship', focusId);
  const { features } = useCareerFocusFeatures();
  const { darkMode } = useDarkMode();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { markDashboardTourCompleted } = useDashboardTourStatus();
  
  // Get initial tab from URL or default to 'goals'
  const getInitialTab = (): 'goals' | 'rubric' | 'evidence' | 'results' | 'reviewprep' => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['goals', 'rubric', 'evidence', 'results', 'reviewprep'].includes(tabParam)) {
      return tabParam as 'goals' | 'rubric' | 'evidence' | 'results' | 'reviewprep';
    }
    return 'goals';
  };
  
  const [activeTab, setActiveTab] = useState<'goals' | 'rubric' | 'evidence' | 'results' | 'reviewprep'>(getInitialTab());
  const [showTour, setShowTour] = useState(false);
  const [quickAddContext, setQuickAddContext] = useState<{
    title?: string;
    placeholder?: string;
    defaultImpact?: number;
    defaultEngagement?: number;
    rubricItemId?: string;
  } | undefined>();
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [isCondensed, setIsCondensed] = useState(false); // Start with timeline expanded
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  // Extract stable focus ID and add debugging
  const stableFocusId = focus?.id || undefined;
  
  // Debug rapid re-renders
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[InternshipDashboard] Render with focus:', {
        focusId: stableFocusId,
        focusLoading,
        focusError: focusError?.message,
        timestamp: Date.now()
      });
    }
  }, [stableFocusId, focusLoading, focusError]);
  
  // Fetch goals and evidence data - must be called before any returns
  const { 
    goals, 
    loading: goalsLoading, 
    error: goalsError,
    refreshGoals 
  } = useGoals(stableFocusId);
  
  const { 
    activeRubric, 
    rubricLoading,
    refetchRubric: refreshRubric 
  } = useRubric(stableFocusId);
  
  const { 
    cards, 
    isLoading: cardsLoading,
    error: cardsError,
    refetch: refreshCards 
  } = useCareerCards(); // Get all cards, filtering will be done in the Evidence Library tab

  // Use centralized phase calculation hook
  const { currentPhase, totalWeeks } = useInternshipPhase(focus);
  
  // Check if this is the default focus - must be called before any conditional returns
  const { defaultFocusId } = useDefaultFocus();
  const isDefaultFocus = defaultFocusId === focus?.id;
  
  // Update URL when tab changes (optional - remove if you don't want URL updates)
  useEffect(() => {
    // Clear the tab parameter from URL after initial load
    // This prevents the URL from staying as ?tab=results when switching tabs
    if (searchParams.get('tab')) {
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Handle scroll detection for timeline docking effect
  useEffect(() => {
    if (!focus) return;
    
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      const scrollContainer = scrollContainerRef.current;
      
      if (!scrollContainer) {
        logger.debug('No scroll container found');
        return;
      }

      // Store the initial timeline offset (distance from top of scrollable container)
      let timelineInitialOffset = 0;
      const timeline = timelineRef.current;
      if (timeline) {
        // Calculate the timeline's offset from the top of the scrollable container
        const headerElement = document.querySelector('.dashboard-header');
        const headerHeight = headerElement ? headerElement.getBoundingClientRect().height : 0;
        timelineInitialOffset = timeline.offsetTop;
        logger.debug('Timeline initial offset:', { timelineInitialOffset, headerHeight });
      }

      const handleScroll = () => {
        const timeline = timelineRef.current;
        if (!timeline) return;
        
        // Check if timeline is visible in viewport
        const rect = timeline.getBoundingClientRect();
        const headerElement = document.querySelector('.dashboard-header');
        const headerBottom = headerElement ? headerElement.getBoundingClientRect().bottom : 0;
        
        // Timeline is out of view when its bottom edge goes above the header bottom
        const isTimelineOutOfView = rect.bottom < headerBottom;
        
        setIsCondensed(isTimelineOutOfView);
        
        logger.debug('Timeline visibility', { 
          timelineBottom: rect.bottom, 
          headerBottom, 
          isOutOfView: isTimelineOutOfView 
        });
      };

      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      
      // Cleanup function
      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    }, 200); // Increased delay to ensure refs are connected

    return () => clearTimeout(timer);
  }, [focus]);

  // Check feature flag
  if (!features.internshipDashboardV2) {
    // Return old dashboard or redirect
    return null;
  }

  if (focusLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (focusError || !focus) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-red-500 mb-4">Unable to load internship dashboard</p>
          <button 
            onClick={() => navigate('/home')}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }
  
  const bgColor = darkMode ? 'bg-gray-900' : 'bg-gray-50';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  
  const handleQuickAdd = () => {
    setShowQuickAddModal(true);
  };

  // Calculate goals progress
  const goalsProgress = {
    total: goals?.length || 0,
    withEvidence: goals?.filter(g => (g.evidence_count || 0) > 0).length || 0
  };

  // Define tour steps
  const tourSteps: TourStep[] = [
    {
      title: "Welcome to Your Internship Dashboard! 👋",
      content: "This dashboard helps you track your progress and build evidence for your goals throughout your internship. Let's show you around!",
      position: "center"
    },
    {
      element: ".timeline-progress",
      title: "Timeline Progress",
      content: `You're in Week ${focus?.current_week || 1} of ${totalWeeks}. This timeline shows where you are in your internship journey - plenty of time to build a strong case for a return offer!`,
      position: "bottom"
    },
    {
      element: ".mission-control",
      title: "Current Phase & Priorities",
      content: `You're in the ${currentPhase} phase. Each phase has different priorities to help you focus on what matters most. The priorities shown here are your recommended actions for this week.`,
      position: "bottom"
    },
    {
      element: ".quick-add-btn",
      title: "Add Evidence Button",
      content: "Your most important action! Click this button to document your work, learnings, and achievements. Regular documentation is key to success.",
      position: "left"
    },
    {
      element: ".tabs-container",
      title: "Main Navigation Tabs",
      content: "Navigate between your personal goals, the company's evaluation rubric, and your evidence library. Each section gives you a different view of your progress.",
      position: "top"
    },
    {
      element: ".focus-mode-slider",
      title: "Focus Mode Toggle",
      content: "Use this to switch between your regular dashboard and this focused internship view. Focus Mode helps you concentrate on internship-specific goals.",
      position: "bottom"
    }
  ];

  return (
    <PhaseGuidanceProvider industryType={focus?.industry_type}>
      <div className={`h-full flex flex-col ${bgColor} ${textColor}`}>
      {/* Dashboard Header - Sticky */}
      <StandardFocusHeader
        focus={focus}
        actionButton={{
          label: 'Quick Add',
          icon: <PlusIcon className="w-4 h-4" />,
          onClick: handleQuickAdd,
          variant: 'primary'
        }}
        isCondensed={isCondensed}
        isDefaultFocus={isDefaultFocus}
        onStartTour={() => {
          // Small delay to ensure all elements are rendered
          setTimeout(() => setShowTour(true), 100);
        }}
        condensedContent={
          <div className="px-3 sm:px-6 py-2">
            <div className="relative group">
              {/* Condensed Timeline Bar */}
              <div className={`h-2 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-full overflow-hidden`}>
                <motion.div 
                  className="h-full bg-gradient-to-r from-blue-500 to-blue-600"
                  animate={{ width: `${Math.min(focus?.progress_percentage || 0, 100)}%` }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                />
              </div>
              
              {/* Hover tooltip */}
              <div className="absolute left-1/2 -translate-x-1/2 -top-8 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-gray-800 text-white text-xs px-2 py-1 rounded pointer-events-none whitespace-nowrap">
                Week {focus?.current_week || 0} of {totalWeeks} • {currentPhase} Phase • {Math.round(focus?.progress_percentage || 0)}%
              </div>
            </div>
          </div>
        }
      />

      {/* Completion Banner */}
      {isCompleted && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`${darkMode ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'} 
            border-b px-6 py-3`}
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-500 text-white">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className={`font-medium ${darkMode ? 'text-green-300' : 'text-green-800'}`}>
                  Internship Completed! 🎉
                </p>
                <p className={`text-sm ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                  {completionMetadata?.daysSinceEnd === 0 
                    ? 'Completed today'
                    : completionMetadata?.daysSinceEnd === 1
                    ? 'Completed yesterday'
                    : `Completed ${completionMetadata?.daysSinceEnd} days ago`}
                  {' • '}All your evidence cards and documentation are preserved here for future reference.
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/focus')}
              className={`text-sm px-4 py-2 rounded-lg transition-colors ${
                darkMode 
                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
                  : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300'
              }`}
            >
              Manage Focuses
            </button>
          </div>
        </motion.div>
      )}

      {/* Scrollable Content Area */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
        {/* Timeline Progress - Part of scrollable content */}
        <TimelineProgress 
          focus={focus} 
          timelineRef={timelineRef}
        />
        
        <div className="max-w-7xl mx-auto px-6 py-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            {/* Mission Control */}
            <MissionControl 
              currentPhase={currentPhase}
              focus={focus}
              onQuickAdd={(context) => {
                setQuickAddContext(context);
                setShowQuickAddModal(true);
              }}
            />

            {/* Progress Widgets */}
            <ProgressWidgets 
              focus={focus}
              goals={goals}
              goalsLoading={goalsLoading}
              cards={cards || []}
              cardsLoading={cardsLoading}
              activeRubric={activeRubric}
              rubricLoading={rubricLoading}
            />

            {/* Main Content Area with Tabs */}
            <TabContainer
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              focus={focus}
              // Goals data
              goals={goals}
              goalsLoading={goalsLoading}
              goalsError={goalsError}
              refreshGoals={refreshGoals}
              // Rubric data
              activeRubric={activeRubric}
              rubricLoading={rubricLoading}
              rubricError={null} // useRubric doesn't expose error
              refreshRubric={refreshRubric}
              // Cards data
              cards={cards || []}
              cardsLoading={cardsLoading}
              cardsError={cardsError}
              refreshCards={refreshCards}
              // Additional props
              onQuickAdd={(rubricItemId, category, description) => {
                if (rubricItemId) {
                  setQuickAddContext({ 
                    rubricItemId,
                    rubricCategory: category,
                    rubricDescription: description,
                    title: 'Add Evidence',
                    placeholder: 'Describe what you accomplished and how it demonstrates this skill...'
                  });
                }
                setShowQuickAddModal(true);
              }}
            />
          </motion.div>
        </div>
      </div>

      {/* Tour Overlay */}
      {showTour && (
        <TourOverlay
          isOpen={showTour}
          onClose={() => setShowTour(false)}
          steps={tourSteps}
          onComplete={() => {
            setShowTour(false);
            markDashboardTourCompleted();
          }}
        />
      )}

      {/* Enhanced Quick Add Modal */}
      <EnhancedQuickAddModal
        isOpen={showQuickAddModal}
        onClose={() => {
          setShowQuickAddModal(false);
          setQuickAddContext(undefined);
        }}
        context={quickAddContext}
        onSuccess={() => {
          // Refresh data after adding evidence
          refreshCards();
        }}
      />
    </div>
    </PhaseGuidanceProvider>
  );
};

export default InternshipDashboardV2;